import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react'
import { Dropdown, Form, Pagination, Table } from 'react-bootstrap'
import { useIntl } from 'react-intl'
import threeDots from '../../../../_metronic/assets/images/svg/investor/threeDots.svg'
import { useDateFormat } from '../../../hooks/useDateFormat'
import NoDataPage from '../../widgets/components/NoDataPage'
import { useAuth } from '../../auth'
import {
  deleteTemplateApi,
  duplicateTemplateTokenApi,
  getTemplateApi,
  getTemplatesApi,
  getTemplateTokenApi,
} from '../core/_requests'
import { useDigitalSigning } from '../core/DigitalSigningContext'
import { toast } from 'react-toastify'
import TemplateView from '../view/TemplateView'
import { TemplateDetails } from '../core/_models'
import SkeletonRow from './SkeletonRow'
import { DeleteTemplateConfirmationModal } from './DeleteTemplateConfirmationModal'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../hooks/gtmHelpers'

const DigitalSigningTemplates = ({
  templatesSearch,
  isTemplateCreateOrEdit,
  setTemplateDetails,
  setShowModal,
  setIsTemplateCreateOrEdit,
  setCurrentTemplateStep,
  isTemplateVisible,
  getTemplatesDashboardCounts,
  viewTemplateLoading,
  setViewTemplateLoading,
}: {
  templatesSearch: string
  isTemplateCreateOrEdit: boolean
  setTemplateDetails: Dispatch<SetStateAction<TemplateDetails | undefined>>
  setShowModal: Dispatch<SetStateAction<boolean>>
  setIsTemplateCreateOrEdit: Dispatch<SetStateAction<boolean>>
  setCurrentTemplateStep: Dispatch<SetStateAction<number>>
  isTemplateVisible: boolean
  getTemplatesDashboardCounts: () => {}
  viewTemplateLoading: boolean
  setViewTemplateLoading: Dispatch<SetStateAction<boolean>>
}) => {
  const { formatMessage } = useIntl()
  const [totalPages, setTotalPages] = useState(0)
  const [digitalSigningTemplateCurrentPage, setDigitalSigningTemplateCurrentPage] =
    useState<number>(1)
  const { getDateValue } = useDateFormat()
  const [templates, setTemplates] = useState<any>()
  const { companyId, currentUser } = useAuth()
  const [limitPerPage, setLimitPerPage] = useState(10)
  const [signingTemplateTotal, setSigningTemplateTotal] = useState(0)
  const didMount = useRef(false)
  const { selectedTemplateTypeFilter } = useDigitalSigning()
  const [showDeleteModal, setDeleteShowModal] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [showViewTemlateModal, setShowViewTemplateModal] = useState(false)
  const [viewToken, setViewToken] = useState()
  const [templateName, setTemplateName] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>()
  const [templateId, setTemplateId] = useState<number>()

  const handleGetTemplateToken = async (templateId: number) => {
    setViewTemplateLoading(true)
    try {
      const {
        data: {
          data: { token },
        },
      } = await getTemplateTokenApi(isTemplateVisible, undefined, undefined, companyId, templateId)
      if (token) {
        setViewToken(token)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setViewTemplateLoading(false)
    }
  }

  const handleDuplicateTemplate = async (templateId: number) => {
    setViewTemplateLoading(true)
    try {
      const {
        data: { data },
      } = await duplicateTemplateTokenApi(templateId)
      if (data) {
        setTemplateDetails(data)
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningTemplateDublicateSuccessful,
            action: 'normal',
            metadata: {
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
              template_id: templateId,
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DigitalSigningTemplateDublicateFailed,
          action: 'normal',
          metadata: {
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            template_id: templateId,
            error: error,
          },
        },
      })
    } finally {
      setViewTemplateLoading(false)
    }
  }

  async function getTemplates() {
    setLoading(true)
    try {
      const {
        data: { data },
      } = await getTemplatesApi(
        companyId!,
        digitalSigningTemplateCurrentPage,
        limitPerPage,
        templatesSearch,
        selectedTemplateTypeFilter
      )
      setTemplates(data.templates)
      setSigningTemplateTotal(data.total)
      setIsTemplateCreateOrEdit(false)
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setTotalPages(Math.ceil(signingTemplateTotal / limitPerPage))
  }, [signingTemplateTotal, limitPerPage])

  useEffect(() => {
    let debounceTimeout: NodeJS.Timeout | null = null
    if (didMount.current) {
      setDigitalSigningTemplateCurrentPage(1)
      if (debounceTimeout) clearTimeout(debounceTimeout)
      debounceTimeout = setTimeout(() => {
        getTemplates()
      }, 1000)
      return () => {
        if (debounceTimeout) clearTimeout(debounceTimeout)
      }
    } else didMount.current = true
  }, [templatesSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getTemplates()
    // eslint-disable-next-line
  }, [
    selectedTemplateTypeFilter,
    digitalSigningTemplateCurrentPage,
    limitPerPage,
    isTemplateCreateOrEdit,
  ])

  const renderPaginationItems = () => {
    const items = []
    const visiblePageCount = 5 // Number of visible page numbers

    // Calculate the range of visible pages based on the current page
    let startPage = Math.max(
      1,
      digitalSigningTemplateCurrentPage - Math.floor(visiblePageCount / 2)
    )
    let endPage = Math.min(totalPages, startPage + visiblePageCount - 1)

    // Adjust the range if it exceeds the total number of pages
    if (endPage - startPage + 1 < visiblePageCount) {
      startPage = Math.max(1, endPage - visiblePageCount + 1)
    }

    // Add the First and Prev buttons
    items.push(
      <Pagination.First
        key='first'
        disabled={digitalSigningTemplateCurrentPage === 1}
        onClick={() => handleClick(1)}
      />
    )
    items.push(
      <Pagination.Prev
        key='prev'
        disabled={digitalSigningTemplateCurrentPage === 1}
        onClick={() => handleClick(digitalSigningTemplateCurrentPage - 1)}
      />
    )

    // Add the page numbers and Ellipsis as needed
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === digitalSigningTemplateCurrentPage}
          onClick={() => handleClick(page)}
        >
          {page}
        </Pagination.Item>
      )
    }

    if (startPage > 1) {
      items.splice(2, 0, <Pagination.Ellipsis key='ellipsis1' />)
    }
    if (endPage < totalPages) {
      items.splice(items.length, 0, <Pagination.Ellipsis key='ellipsis2' />)
    }

    // Add the Next and Last buttons
    items.push(
      <Pagination.Next
        key='next'
        disabled={digitalSigningTemplateCurrentPage === totalPages}
        onClick={() => handleClick(digitalSigningTemplateCurrentPage + 1)}
      />
    )
    items.push(
      <Pagination.Last
        key='last'
        disabled={digitalSigningTemplateCurrentPage === totalPages}
        onClick={() => handleClick(totalPages)}
      />
    )

    return items
  }

  const handleClick = (pageNumber: number) => {
    setDigitalSigningTemplateCurrentPage(pageNumber)
  }

  const getTemplate = async (templateId: number) => {
    setViewTemplateLoading(true)
    try {
      const {
        data: { data },
      } = await getTemplateApi(templateId!, companyId!)

      if (data.templateDetails) {
        setTemplateDetails(data.templateDetails)
      } else {
        toast.error(formatMessage({ id: data.errors[0] }))
      }
    } catch (error) {
      console.log(error)
    } finally {
      setViewTemplateLoading(false)
    }
  }

  const handleDeleteTemplate = async () => {
    setDeleteLoading(true)
    try {
      const {
        data: { data },
      } = await deleteTemplateApi(templateId!)
      if (data) {
        toast.success(formatMessage({ id: 'Template deleted successfully' }))
        await getTemplates()
        await getTemplatesDashboardCounts()
        TagManager.dataLayer({
          dataLayer: {
            event: GTMEvent.DigitalSigningTemplateDeleteSuccessful,
            metadata: {
              template_id: templateId,
              user_id: currentUser?.userId,
              email: currentUser?.email,
              company_id: companyId,
            },
          },
        })
      }
    } catch (error) {
      console.log(error)
      TagManager.dataLayer({
        dataLayer: {
          event: GTMEvent.DigitalSigningTemplateDeleteFailed,
          metadata: {
            template_id: templateId,
            user_id: currentUser?.userId,
            email: currentUser?.email,
            company_id: companyId,
            error: error,
          },
        },
      })
    } finally {
      setDeleteLoading(false)
      setDeleteShowModal(false)
    }
  }

  return (
    <>
      <div className='card mt-5'>
        <div className='bg-white rounded p-6 position-relative'>
          {!loading && templates?.length === 0 ? (
            <div className='my-5 mx-6'>
              <NoDataPage width={'w-0'} />
            </div>
          ) : (
            <div className='table table-responsive h-100'>
              <Table className='w-100 '>
                <thead className='custom-thead border-bottom'>
                  <tr className='font-size-12 font-weight-400 table-row border-dark-mode'>
                    <td className='min-w-120px'>{formatMessage({ id: 'Title' })}</td>
                    <td className='min-w-120px'>{formatMessage({ id: 'Type' })}</td>
                    <td className='min-w-120px'>{formatMessage({ id: 'Creation Date' })}</td>
                    <td className='min-w-120px'>{formatMessage({ id: 'Created By' })}</td>
                    <td className='min-w-120px'>{formatMessage({ id: 'Usage Count' })}</td>
                    <td className='min-w-120px'>{formatMessage({ id: 'Last Used' })}</td>
                    <td className='min-w-120px d-flex justify-content-center border-0'>
                      {formatMessage({ id: 'Actions' })}
                    </td>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <>
                      <SkeletonRow count={8} />
                    </>
                  ) : (
                    <>
                      {templates?.map((template: any, index: number) => (
                        <tr
                          key={template.id}
                          className='border-bottom cursor-pointer'
                          onClick={() => {
                            setTemplateName(template.title)
                            setShowViewTemplateModal(true)
                            handleGetTemplateToken(template.templateId)
                          }}
                        >
                          <td className='pt-4 text-start'>
                            <div className='d-flex align-items-center '>
                              <p className='mb-0 text-width-mobile'>{template.title}</p>
                            </div>
                          </td>
                          <td className='pt-4'>
                            <div className='badge badge-light-success py-2 px-6'>
                              <p className='mb-0'>{template.type}</p>
                            </div>
                          </td>
                          <td className='pt-4'>
                            <div>
                              <p className='mb-0 text-width-mobile'>
                                {getDateValue(
                                  new Date(template.createdAt).toISOString()?.toString()
                                )}
                              </p>
                            </div>
                          </td>
                          <td className='pt-4'>
                            <div>
                              {' '}
                              <p className='mb-0 text-width-mobile'>{`${template.user.firstName} ${template.user.lastName}`}</p>
                            </div>
                          </td>
                          <td className='pt-4'>
                            <div>
                              {' '}
                              <p className='mb-0 text-width-mobile'>
                                {template.document_submissions.length}
                              </p>
                            </div>
                          </td>
                          <td className='pt-4'>
                            <div>
                              <p className='mb-0 text-width-mobile'>
                                {getDateValue(
                                  new Date(template.updatedAt).toISOString()?.toString()
                                )}
                              </p>
                            </div>
                          </td>
                          <td className='pt-3' onClick={(e) => e.stopPropagation()}>
                            <div className='dropdown-container d-flex justify-content-center'>
                              <Dropdown
                                show={index === selectedIndex}
                                onToggle={(isOpen, event: any) => {
                                  if (event.source === 'click') {
                                    setSelectedIndex(isOpen ? index : undefined)
                                  } else {
                                    setSelectedIndex(undefined)
                                  }
                                }}
                              >
                                <Dropdown.Toggle className='btn btn-white table-three-dots dots-container '>
                                  <img src={threeDots} alt='' className='three-dots-image' />
                                </Dropdown.Toggle>
                                <Dropdown.Menu className='m-2'>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      setTemplateName(template.title)
                                      setShowViewTemplateModal(true)
                                      handleGetTemplateToken(template.templateId)
                                      TagManager.dataLayer({
                                        dataLayer: {
                                          event: GTMEvent.DigitalSigningTemplateViewedSuccessful,
                                          action: 'normal',
                                          metadata: {
                                            user_id: currentUser?.userId,
                                            email: currentUser?.email,
                                            company_id: companyId,
                                            template_id: templateId,
                                            title: template.title || '',
                                            type: template.type || '',
                                          },
                                        },
                                      })
                                    }}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'View Template' })}</p>
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      setCurrentTemplateStep(1)
                                      setShowModal(true)
                                      getTemplate(template.templateId)
                                    }}
                                  >
                                    {formatMessage({ id: 'Edit Template' })}
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    className='text-dark ps-5'
                                    onClick={() => {
                                      setCurrentTemplateStep(1)
                                      setShowModal(true)
                                      handleDuplicateTemplate(template.templateId)
                                    }}
                                  >
                                    {formatMessage({ id: 'Duplicate Template' })}
                                  </Dropdown.Item>

                                  <Dropdown.Item
                                    className='text-danger ps-5'
                                    onClick={() => {
                                      setTemplateId(template.templateId)
                                      setDeleteShowModal(true)
                                    }}
                                  >
                                    <p className='mb-0'>{formatMessage({ id: 'Delete Template' })}</p>
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </>
                  )}
                </tbody>
              </Table>
            </div>
          )}
          {!loading && templates?.length > 0 && signingTemplateTotal > 10 && (
            <div className='w-100 d-flex justify-content-between align-items-center flex-wrap flex-md-nowrap'>
              <div className='d-flex align-items-center w-100 w-md-50 mb-md-0 mb-4'>
                <Form.Select
                  aria-label='Default select example'
                  onChange={(e) => {
                    setLimitPerPage(parseInt(e.target.value))
                    setDigitalSigningTemplateCurrentPage(1)
                  }}
                  defaultValue={limitPerPage}
                >
                  <option value={10}>{formatMessage({ id: '10' })}</option>
                  <option value={20}>{formatMessage({ id: '20' })}</option>
                  <option value={50}>{formatMessage({ id: '50' })}</option>
                  <option value={100}>{formatMessage({ id: '100' })}</option>
                </Form.Select>
                <span className='font-size-13 ms-3'>
                  {formatMessage({
                    id: `Showing 1 to ${templates?.length} of ${signingTemplateTotal} records`,
                  })}
                </span>
              </div>
              <Pagination>{renderPaginationItems()}</Pagination>
            </div>
          )}
        </div>
      </div>

      <TemplateView
        showViewTemlateModal={showViewTemlateModal}
        setShowViewTemplateModal={setShowViewTemplateModal}
        token={viewToken}
        templateName={templateName}
        viewTemplateLoading={viewTemplateLoading}
      />

      <DeleteTemplateConfirmationModal
        setDeleteShowModal={setDeleteShowModal}
        showDeleteModal={showDeleteModal}
        onDeleteSubmit={handleDeleteTemplate}
        deleteLoading={deleteLoading}
      />
    </>
  )
}

export default DigitalSigningTemplates
